import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { ICON_NAMES } from '../../../../../../constants'
import SydButton from '../../../../../commonDesign/Button'
import FlexColumn from '../../../../../molecules/FlexColumn'
import { toURLSafeBase64 } from '../../../../../../utils/URLSafeBase64'
import { useCreateOutboundRedirect } from '../../../../../../api/users/redirect'
import {
  useCreatePendingAuthorization,
  usePollPendingAuthorizationStatus
} from '../../../../../../api/users/authorizations'

function getAuthUrl (authorizationRef, config, fieldValues = {}) {
  const url = new URL(config.authorization_url)
  const state = toURLSafeBase64(authorizationRef.authorizationId)
  const queryParams = new URLSearchParams({
    client_id: fieldValues.client_id || config.client_id,
    redirect_uri: config.redirect_uri,
    response_type: config.response_type,
    scope: config.scope,
    state
  })
  url.search = queryParams.toString()

  return [url.toString(), state]
}

const usePopupFlow = (config, onFlowComplete, authRequiredFieldValues) => {
  const { mutateAsync: createOutboundRedirect } = useCreateOutboundRedirect()
  const { mutateAsync: createAuthorization } = useCreatePendingAuthorization()
  const { mutateAsync: pollStatus } = usePollPendingAuthorizationStatus()

  const [completed, setCompleted] = useState(false)
  const [processing, setProcessing] = useState(false)
  const popupRef = useRef(null)
  const pollRef = useRef(null)

  const handleOutboundRedirect = useCallback(async ({ to, from, purpose }) => {
    const response = await createOutboundRedirect({
      to,
      from,
      purpose,
      label: 'Redirecting...',
      method: 'GET'
    })

    const popup = window.open(
      `${window.location.origin}/external/redirect/${response.redirect.redirectId}`,
      'external_auth',
      'width=500,height=600,top=100,left=100,resizable,scrollbars'
    )
    if (popup) popup.opener = null
    popupRef.current = popup
  }, [createOutboundRedirect])

  const handleClick = useCallback(async () => {
    setProcessing(true)

    // we create a pending authorization to track the status, because we might move across domains
    const authorizationReservation = await createAuthorization({
      purpose: config.domain,
      payload: {
        from: window.location.toString(),
        authRequiredFields: authRequiredFieldValues
      }
    })
    // next use that authorization to create the redirect URL
    const [authUrl, stateKey] = getAuthUrl(authorizationReservation.authorization, config, authRequiredFieldValues)

    pollRef.current = {
      authorizationId: authorizationReservation.authorization.authorizationId,
      purpose: config.domain,
      completed: false
    }
    await handleOutboundRedirect({
      to: authUrl,
      from: window.location.toString(),
      purpose: config.domain
    })

    return stateKey
  }, [config, createAuthorization, setProcessing, handleOutboundRedirect, authRequiredFieldValues])

  useEffect(() => {
    if (!processing || pollRef.current?.completed) {
      return
    }

    async function handlePoll () {
      if (!pollRef.current) {
        console.error('no pollRef')
        window.setTimeout(handlePoll, 5000)
      }
      const response = await pollStatus(pollRef.current)

      if (response?.authorizationStatus?.resolved) {
        setProcessing(false)
        setCompleted(true)
        pollRef.current.completed = true
        onFlowComplete(pollRef.current, popupRef.current)
        if (popupRef.current) {
          popupRef.current.close()
          popupRef.current = null
        }
      } else if (!response?.authorizationStatus) {
        alert('There was a problem authorizing the integration. Please try again.')
        setProcessing(false)
        pollRef.current = null
      } else {
        window.setTimeout(handlePoll, 5000)
      }
    }

    window.setTimeout(handlePoll, 5000)
  }, [onFlowComplete, pollStatus, processing, setProcessing])

  return {
    handleClick,
    completed,
    processing
  }
}

function InitiateOAuthFlowButton ({ config, label, onChange, value, disabled, authRequiredFieldValues }) {
  const handleFlowCompleted = useCallback((data) => {
    onChange(data.authorizationId)
  }, [onChange])
  const { handleClick, processing } = usePopupFlow(config, handleFlowCompleted, authRequiredFieldValues)

  return (
    <FlexColumn>
      <SydButton
        variant='primary'
        size='lg'
        icon={ICON_NAMES.link}
        onClick={handleClick}
        disabled={!!value || disabled}
        processing={processing}
        style={{
          backgroundColor: value ? 'var(--color-success-500)' : 'var(--color-primary-500)',
          color: value ? 'var(--color-contrast-success-500)' : 'var(--color-contrast-primary-500)',
          fill: value ? 'var(--color-contrast-success-500)' : 'var(--color-contrast-primary-500)',
          opacity: (disabled && !value) ? 0.5 : 1
        }}
      >
        {value ? 'Authorized' : label}
      </SydButton>
    </FlexColumn>
  )
}

InitiateOAuthFlowButton.propTypes = {
  config: PropTypes.shape({
    authorization_url: PropTypes.string,
    client_id: PropTypes.string,
    redirect_uri: PropTypes.string,
    response_type: PropTypes.string,
    scope: PropTypes.string,
    domain: PropTypes.string
  }),
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  authRequiredFieldValues: PropTypes.object
}

InitiateOAuthFlowButton.defaultProps = {
  label: 'Connect'
}

export default InitiateOAuthFlowButton
